import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Button, CircularProgress, Tooltip } from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import HelpIcon from "@material-ui/icons/Help";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";

import Home from "../Home/Home";
import {
  getInboxMessages,
  getDraftStats,
  getAvatars,
  getAgent,
  getWatch,
  startWatch,
  stopWatch,
  fetchDraft,
} from "../../Redux/Actions/agent";
import {
  selectInboxMessages,
  selectAvatars,
  selectDraftStats,
  selectAgent,
  selectMailboxWatch,
} from "../../Redux/Selectors";
import DraftPanel from "./DraftPanel";
import {
  draftChip,
  videoChip,
  draftIdsToPoll,
  formatDate,
  hasPendingDrafts,
  DRAFT_POLL_MAX_MS,
} from "./status";
import "../ai-shared.css";

const POLL_MS = 10000;
const DRAFT_POLL_MS = 5000;

const CLASSIFICATION_LABEL: Record<string, string> = {
  interested: "Interested",
  needs_followup: "Needs follow-up",
  not_interested: "Not interested",
  out_of_office: "Out of office",
  not_a_reply: "Not a reply",
};

const Inbox: React.FC = () => {
  const dispatch = useDispatch();
  const messages: any[] = useSelector(selectInboxMessages);
  const avatars: any[] = useSelector(selectAvatars);
  const stats: any = useSelector(selectDraftStats);
  const agent: any = useSelector(selectAgent);
  const watch: any = useSelector(selectMailboxWatch);
  const loading: boolean = useSelector((state: any) => state.agent.messagesLoading);
  const error: string | null = useSelector((state: any) => state.agent.messagesError);
  const watchLoading: boolean = useSelector((state: any) => state.agent.watchLoading);
  const draftPending: Record<string, string> = useSelector((state: any) => state.agent.draftPending);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    dispatch(getInboxMessages());
    dispatch(getDraftStats());
    dispatch(getAvatars());
    dispatch(getWatch());
    dispatch(getAgent());
  }, [dispatch]);

  // Poll drafts whose video is rendering or whose send is in flight.
  const pollIds = draftIdsToPoll(messages).join(",");
  useEffect(() => {
    if (!pollIds) return undefined;
    const timer = window.setInterval(() => {
      pollIds.split(",").forEach((id) => dispatch(fetchDraft(id)));
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [pollIds, dispatch]);

  // Drafts written in the background (202 from generate): re-read the inbox
  // every 5s while any message is pending, for at most ~3 minutes.
  // Keyed by the pending ids so a newly queued draft restarts the window.
  const pendingDraftKey = hasPendingDrafts(messages)
    ? messages
        .filter((message) => message.draftPending)
        .map((message) => message.id)
        .join(",")
    : "";
  const [draftPollExpired, setDraftPollExpired] = useState(false);
  useEffect(() => {
    if (!pendingDraftKey) return undefined;
    setDraftPollExpired(false);
    const timer = window.setInterval(() => dispatch(getInboxMessages()), DRAFT_POLL_MS);
    const cap = window.setTimeout(() => {
      window.clearInterval(timer);
      setDraftPollExpired(true);
    }, DRAFT_POLL_MAX_MS);
    return () => {
      window.clearInterval(timer);
      window.clearTimeout(cap);
    };
  }, [pendingDraftKey, dispatch]);

  const refresh = () => {
    dispatch(getInboxMessages());
    dispatch(getDraftStats());
    dispatch(getWatch());
  };

  const videoEnabled = !agent || agent.videoMode !== "off";
  const selected = messages.find((message) => message.id === selectedId) || null;

  const renderRow = (message: any) => {
    const chips = [draftChip(message), videoEnabled ? videoChip(message.draft) : null];
    const classification = CLASSIFICATION_LABEL[message.classification];
    return (
      <button
        type="button"
        key={message.id}
        className={`inboxRow ${selectedId === message.id ? "inboxRowActive" : ""}`}
        onClick={() => setSelectedId(message.id)}
      >
        <div className="inboxRowTop">
          <span className="inboxFrom">{message.fromName || message.fromEmail}</span>
          <span>{message.receivedAt ? new Date(message.receivedAt).toLocaleDateString() : ""}</span>
        </div>
        <div className="inboxSubject">{message.subject || "(no subject)"}</div>
        <div className="inboxSnippet">{message.snippet || message.bodyText}</div>
        <div className="aiChips">
          {chips.map(
            (chip) =>
              chip && (
                <span key={chip.label} className={`aiChip aiChip--${chip.tone}`}>
                  {chip.label}
                </span>
              )
          )}
          {classification && <span className="aiMuted">{classification}</span>}
        </div>
      </button>
    );
  };

  const renderSync = () => {
    if (!watch) return null;
    if (!watch.connected) {
      return (
        <div className="inboxSync">
          <span className="aiChip aiChip--muted">Gmail not connected</span>
          <span className="aiMuted">
            <Link to="/configuration">Connect Gmail</Link> to pull in replies automatically.
          </span>
        </div>
      );
    }
    if (watch.active) {
      return (
        <div className="inboxSync">
          <span className="aiChip aiChip--ok">Live sync on</span>
          <span className="aiMuted">
            {watch.emailAddress}
            {watch.expiresAt ? ` · active until ${formatDate(watch.expiresAt)}` : ""}
          </span>
          <Button size="small" disabled={watchLoading} onClick={() => dispatch(stopWatch())}>
            Stop
          </Button>
        </div>
      );
    }
    return (
      <div className="inboxSync">
        <span className="aiChip aiChip--muted">Live sync off</span>
        <span className="aiMuted">{watch.emailAddress}</span>
        <Button
          size="small"
          variant="contained"
          className="aiPrimaryButton"
          disabled={watchLoading}
          onClick={() => dispatch(startWatch())}
        >
          {watchLoading ? "Enabling..." : "Enable live sync"}
        </Button>
      </div>
    );
  };

  const renderBody = () => {
    if (loading && messages.length === 0) {
      return (
        <div className="aiLoading">
          <CircularProgress />
        </div>
      );
    }
    if (error && messages.length === 0) {
      return (
        <div className="aiCard">
          <div className="aiNotice aiNotice--error">Could not load your replies: {error}</div>
          <Button variant="outlined" onClick={refresh}>
            Try again
          </Button>
        </div>
      );
    }
    if (messages.length === 0) {
      return (
        <div className="aiCard aiEmpty">
          No replies yet. Turn on live sync after connecting Gmail and replies will appear here.
        </div>
      );
    }
    return (
      <div className={`inboxLayout ${selected ? "inboxHasSelection" : ""}`}>
        <div className="inboxList">{messages.map(renderRow)}</div>
        <div className="inboxDetail">
          {selected ? (
            <>
              <Button
                size="small"
                className="inboxBack"
                startIcon={<ArrowBackIcon />}
                onClick={() => setSelectedId(null)}
              >
                Back to replies
              </Button>
              <div className="inboxDetailSubject">{selected.subject || "(no subject)"}</div>
              <div className="aiMuted">
                From {selected.fromName || selected.fromEmail} &lt;{selected.fromEmail}&gt;
              </div>
              <div className="inboxOriginBody">{selected.bodyText}</div>
              <div className="inboxDivider" />
              <DraftPanel
                message={selected}
                avatars={avatars}
                videoEnabled={videoEnabled}
                draftPending={draftPending}
                draftPollExpired={draftPollExpired}
              />
            </>
          ) : (
            <div className="aiEmpty">Select a reply to review the agent's draft.</div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Home>
      <div className="aiPage">
        <div className="aiHeader">
          <div>
            <span className="heading">Inbox Replies </span>
            <span className="headingTooltip">
              <Tooltip
                title="Email replies the agent has seen, with its draft answers. Review, edit, approve and send them here."
                placement="right-start"
                arrow
              >
                <HelpIcon />
              </Tooltip>
            </span>
            {stats && (
              <div className="aiSub">
                {stats.inbound ?? 0} received · {stats.pending ?? 0} awaiting review ·{" "}
                {stats.sent ?? 0} sent
                {stats.autoSent ? ` (${stats.autoSent} auto)` : ""}
                {stats.failed ? ` · ${stats.failed} failed` : ""}
              </div>
            )}
          </div>
          <div className="aiActions">
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={refresh}
            >
              Refresh
            </Button>
          </div>
        </div>
        {watch && <div className="aiCard">{renderSync()}</div>}
        {renderBody()}
      </div>
    </Home>
  );
};

export default Inbox;
